import gql from 'graphql-tag';
import { OFFER_BASE_FRAGMENT, OFFER_FRAGMENT } from './fragments/OfferFragment';

// Base offer query without any link or promo data
export const GET_OFFER_BY_ID_BASE_QUERY = gql`
  query GetOfferByIdBase($id: Int!) {
    offer: offerById(id: $id) {
      ...OfferBaseFragment
    }
  }
  ${OFFER_BASE_FRAGMENT}
`;

// Query for promos only (without affiliates or stats)
export const GET_OFFER_PROMOS_ONLY_QUERY = gql`
  query GetOfferPromosOnly($id: Int!) {
    offer: offerById(id: $id) {
      id
      promos {
        codeSuffix
        codePrefix
        disabled
        flatPayout
        id
        name
        offerId
        payoutType
        percentPayout
        prefixType
        priceRuleAmount
        priceRuleType
        source
        specialLimitNewCustomersOnly
        specialLimitOnePerSale
        specialLimitUsageCapAmount
        specialLimitUsageCapEnabled
        status
        defaultPayoutId
        usageLimitAmount
        usageLimitRule
        providerMetadata {
          productCollections {
            id
            title
          }
          discountCombination {
            orderDiscounts
            productDiscounts
            shippingDiscounts
          }
        }
        isSubscription
        isUngrouped
        isOneTime
        recurringCycleLimit
        discountCodeGId
        startDate
        endDate
        multiShopifyEnabled
        isSecureCodes
        connectedClientMetadata{
          advertiserId
          priceRuleType
          priceRuleAmount
          status
          discountCodeGid
        }
      }
    }
  }
`;

// Query for links only (without affiliates or stats)
export const GET_OFFER_LINKS_ONLY_QUERY = gql`
  query GetOfferLinksOnly($id: Int!) {
    offer: offerById(id: $id) {
      id
      links {
        defaultPayoutId
        affiliateOfferLinkGenerated
        conversionTrackingType
        conversionType
        flatPayout
        id
        payoutType
        percentPayout
        pixelCode
        postbackUrl
        status
        url
        utmFields
        domains
        creatorDeeplink
        allowMultipleConversions
        thirdPartyTracking
        trackPayoutInAspire
        isThirdPartyIntegration
        isCreatorDeepLinkEnabled: creatorDeeplink
        shopifyRedirectUrl
      }
    }
  }
`;

// Query for promo affiliates only
export const GET_OFFER_PROMOS_AFFILIATES_QUERY = gql`
  query GetOfferPromosAffiliates($id: Int!) {
    offer: offerById(id: $id) {
      id
      promos {
        id
        affiliates {
          affiliateId
          offerPayoutId
          affiliate {
            email
            memberId
            name
            profilePicture
            status
            deletedDate
          }
          endDate
          externalDiscountCode
          externalDiscountCodeGId
          id
          startDate
          deletedDate
          status
          providerMetadata
          createdDate
          offerAutomationConfigId
          boostDataForAffiliateOffer {
              boost {
                id
                label
                boostType
                status
                isActive
              }
              boostRewardsForAffiliate {
                boostId
                createdDate
                deletedDate
                id
                isActive
                secureCode {
                  externalDiscountCode
                  isRedeemed
                }
                secureCodeId
                status
                updatedDate
              }
            }
        }
      }
    }
  }
`;

// Query for promo affiliate stats only
export const GET_OFFER_PROMOS_AFFILIATE_STATS_QUERY = gql`
  query GetOfferPromosAffiliateStats($id: Int!, $startDate: DateTime, $endDate: DateTime) {
    offer: offerById(id: $id) {
      id
      promos {
        id
        affiliateStats(startDate: $startDate, endDate: $endDate) {
          affiliateCode
          affiliateEmail
          affiliateId
          affiliateImageUrl
          affiliateName
          affiliateOfferId
          aspirexMemberId
          avgSale
          conversions
          payoutEarned
          payoutMade
          sales
          avgSaleBase
          salesBase
          payoutEarnedBase
        }
      }
    }
  }
`;

// Query for promo stats only
export const GET_OFFER_PROMOS_STATS_QUERY = gql`
  query GetOfferPromosStats($id: Int!, $startDate: DateTime, $endDate: DateTime, $status: [String!]) {
    offer: offerById(id: $id) {
      id
      promos {
        id
        stats(startDate: $startDate, endDate: $endDate, status: $status) {
          avgSale
          conversions
          payoutEarned
          payoutMade
          sales
          salesBase
          avgSaleBase
          payoutEarnedBase
          baseCurrencies
        }
      }
    }
  }
`;

// Query for link affiliates only
export const GET_OFFER_LINKS_AFFILIATES_QUERY = gql`
  query GetOfferLinksAffiliates($id: Int!) {
    offer: offerById(id: $id) {
      id
      links {
        id
        affiliates {
          affiliateId
          offerPayoutId
          affiliateDeeplinks {
            id,
            affiliateDeepLinkUrl,
            url,
            label,
            createdDate
            deletedDate,
            shortLink
          }
          affiliate {
            email
            memberId
            name
            profilePicture
            status
            deletedDate
          }
          id
          deletedDate
          createdDate
          updateLog
          deeplinkUrl
          affiliateShortLink
          offerAutomationConfigId
        }
      }
    }
  }
`;

// Query for link affiliate stats only
export const GET_OFFER_LINKS_AFFILIATE_STATS_QUERY = gql`
  query GetOfferLinksAffiliateStats($id: Int!, $startDate: DateTime, $endDate: DateTime) {
    offer: offerById(id: $id) {
      id
      links {
        id
        canEditConversionType
        affiliateStats(startDate: $startDate, endDate: $endDate) {
          affiliateEmail
          affiliateId
          affiliateImageUrl
          affiliateLink
          affiliateShortLink
          affiliateName
          affiliateOfferId
          aspirexMemberId
          avgSale
          clicks
          conversionRatio
          conversions
          linkCreationDate
          linkStatus
          payoutEarned
          payoutMade
          sales
          nonDuplicateConversions
          salesBase
          avgSaleBase
          payoutEarnedBase
        }
      }
    }
  }
`;

// Query for link stats only
export const GET_OFFER_LINKS_STATS_QUERY = gql`
  query GetOfferLinksStats($id: Int!, $startDate: DateTime, $endDate: DateTime) {
    offer: offerById(id: $id) {
      id
      links {
        id
        stats(startDate: $startDate, endDate: $endDate) {
          avgSale
          clicks
          conversionRatio
          conversions
          payoutEarned
          payoutMade
          sales
          nonDuplicateConversions
          salesBase
          avgSaleBase
          payoutEarnedBase
          baseCurrencies
        }
      }
    }
  }
`;

// Legacy query for backward compatibility (deprecated)
export const GET_OFFER_BY_ID_QUERY = gql`
  query GetOfferById($id: Int!, $startDate: DateTime, $endDate: DateTime, $status: [String!]) {
    offer: offerById(id: $id) {
      ...OfferFragment
      links {
        canEditConversionType
        affiliateStats(startDate: $startDate, endDate: $endDate) {
          affiliateEmail
          affiliateId
          affiliateImageUrl
          affiliateLink
          affiliateShortLink
          affiliateName
          affiliateOfferId
          aspirexMemberId
          avgSale
          clicks
          conversionRatio
          conversions
          linkCreationDate
          linkStatus
          payoutEarned
          payoutMade
          sales
          nonDuplicateConversions
          salesBase
          avgSaleBase
          payoutEarnedBase
        }
        stats(startDate: $startDate, endDate: $endDate, status: $status) {
          avgSale
          clicks
          conversionRatio
          conversions
          payoutEarned
          payoutMade
          sales
          nonDuplicateConversions
          salesBase
          avgSaleBase
          payoutEarnedBase
          baseCurrencies
        }
      }
      offerAutomationConfig {
        id
        isActive
        automationType
        automationStartDate
        tierAggregationDuration
        showInCreatorPortal
      }
      promos {
        affiliateStats(startDate: $startDate, endDate: $endDate) {
          affiliateCode
          affiliateEmail
          affiliateId
          affiliateImageUrl
          affiliateName
          affiliateOfferId
          aspirexMemberId
          avgSale
          conversions
          payoutEarned
          payoutMade
          sales
          avgSaleBase
          salesBase
          payoutEarnedBase
        }
        stats(startDate: $startDate, endDate: $endDate, status: $status) {
          avgSale
          conversions
          payoutEarned
          payoutMade
          sales
          salesBase
          avgSaleBase
          payoutEarnedBase
          baseCurrencies
        }
      }
    }
  }
  ${OFFER_FRAGMENT}
`;
